import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { AuditAction, AuditRecord } from "../types";

const actionLabels: Record<AuditAction, string> = {
  create: "Created",
  edit: "Edited",
  remove: "Removed",
  restore: "Restored",
  import: "Imported"
};

const fieldLabels: Record<string, string> = {
  deal_date: "Date",
  time_label: "Time",
  department_name: "Department",
  customer: "Customer",
  vehicle: "Vehicle",
  stock_number: "Stock Number",
  salesperson: "Salesperson",
  fi_manager: "F&I Manager",
  type_notes: "Type / Deal",
  status: "Status"
};

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(new Date(value));
}

function snapshotValue(record: AuditRecord, key: string): string {
  const snapshot = record.afterData ?? record.beforeData;
  const value = snapshot?.[key];
  return value == null ? "" : String(value);
}

function changeSummary(record: AuditRecord): string[] {
  if (record.action === "create") return ["New deal entered"];
  if (record.action === "remove") return ["Deal removed from active results"];
  if (record.action === "restore") return ["Deal restored to active results"];
  if (record.action === "import") return ["Historical deal imported"];

  const before = record.beforeData ?? {};
  const after = record.afterData ?? {};
  return Object.keys(fieldLabels)
    .filter((key) => String(before[key] ?? "") !== String(after[key] ?? ""))
    .map((key) => `${fieldLabels[key]}: ${String(before[key] ?? "—")} → ${String(after[key] ?? "—")}`);
}

export default function AuditHistory() {
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    action: "" as "" | AuditAction,
    query: ""
  });
  const [rows, setRows] = useState<AuditRecord[]>([]);
  const [selected, setSelected] = useState<AuditRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function fetchRows(nextFilters = filters) {
    setLoading(true);
    setError("");
    try {
      const result = await api.listAudit(nextFilters, 500);
      setRows(result);
      if (selected && !result.some((row) => row.id === selected.id)) setSelected(null);
    } catch (caught) {
      setRows([]);
      setError(caught instanceof Error ? caught.message : "Audit history could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  async function load(event?: FormEvent) {
    event?.preventDefault();
    await fetchRows();
  }

  async function clear() {
    const emptyFilters = {dateFrom: "", dateTo: "", action: "" as const, query: ""};
    setFilters(emptyFilters);
    await fetchRows(emptyFilters);
  }

  useEffect(() => { void fetchRows(); }, []);

  const selectedChanges = useMemo(() => selected ? changeSummary(selected) : [], [selected]);

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Permanent database record</span>
            <h2>Audit History</h2>
          </div>
          <strong className="audit-count">{rows.length}</strong>
        </div>

        <form className="audit-form" onSubmit={load}>
          <label>From<input type="date" value={filters.dateFrom} onChange={(e) => setFilters({...filters, dateFrom: e.target.value})} /></label>
          <label>To<input type="date" value={filters.dateTo} onChange={(e) => setFilters({...filters, dateTo: e.target.value})} /></label>
          <label>Action<select value={filters.action} onChange={(e) => setFilters({...filters, action: e.target.value as "" | AuditAction})}><option value="">All actions</option><option value="create">Created</option><option value="edit">Edited</option><option value="remove">Removed</option><option value="restore">Restored</option><option value="import">Imported</option></select></label>
          <label className="audit-keyword">Search<input value={filters.query} onChange={(e) => setFilters({...filters, query: e.target.value})} placeholder="Deal ID, customer, vehicle, stock, user or reason" /></label>
          <button className="primary" disabled={loading}>{loading ? "Loading…" : "Search Audit"}</button>
          <button type="button" className="secondary" disabled={loading} onClick={() => void clear()}>Clear</button>
        </form>

        {error && <p className="error">{error}</p>}

        <div className="table-wrap">
          <table>
            <thead><tr><th>Date &amp; Time</th><th>Action</th><th>Deal ID</th><th>Customer</th><th>Vehicle</th><th>Changed By</th><th>Reason</th><th /></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td><span className={`audit-badge ${row.action}`}>{actionLabels[row.action]}</span></td>
                  <td>{row.dealId}</td>
                  <td>{snapshotValue(row, "customer") || "—"}</td>
                  <td>{snapshotValue(row, "vehicle") || "—"}</td>
                  <td>{row.actorEmail}</td>
                  <td className="audit-reason">{row.reason || "—"}</td>
                  <td><button className="link-button" onClick={() => setSelected(row)}>View Details</button></td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="empty-state">No audit records matched your search.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {selected && (
        <section className="panel audit-detail">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">{selected.dealId}</span>
              <h2>{actionLabels[selected.action]} by {selected.actorEmail}</h2>
            </div>
            <button className="secondary" onClick={() => setSelected(null)}>Close Details</button>
          </div>
          <div className="audit-detail-grid">
            <article><span>Date &amp; Time</span><strong>{formatDateTime(selected.createdAt)}</strong></article>
            <article><span>Customer</span><strong>{snapshotValue(selected, "customer") || "—"}</strong></article>
            <article><span>Vehicle</span><strong>{snapshotValue(selected, "vehicle") || "—"}</strong></article>
            <article><span>Stock Number</span><strong>{snapshotValue(selected, "stock_number") || "—"}</strong></article>
          </div>
          <div className="audit-change-list">
            <h3>Recorded Changes</h3>
            {selectedChanges.length > 0 ? selectedChanges.map((change) => <p key={change}>{change}</p>) : <p className="muted">No field-by-field difference was recorded.</p>}
          </div>
          {selected.reason && <p><strong>Reason:</strong> {selected.reason}</p>}
        </section>
      )}
    </div>
  );
}
